'use client';
import styled, { css } from 'styled-components';
import { LOT_THEME, UnstyledButton } from '../_Common/lot.common.styles';
import { MetricItems } from '../ContentHeader/MetricItems';
import { LotSettingsContext } from '../_Providers/LotProvider';
import { useContext } from 'react';
import { GALLERY_VARIANTS, GalleryVariant } from '../temp_userSettings';
import { theme } from '@/styles/theme'

export const Body = styled.body``
export const PageContainer = styled.div`
  display: grid;
  place-items: center;
  padding: 0 0px;
  transition: padding 1.6s;
  @media screen and (width < ${LOT_THEME.breakpoints.xl}) {
    padding: 0 16px;
  }
`

export const PortalRootDiv = styled.div`
  position: relative;
  z-index: 1;
  top: 0;
`

const repeatArea = (name, times) => Array(times).fill(name).join(' ')

export const Main = styled.main.attrs(() => ({}))<{
  variant: GalleryVariant
}>`
  max-width: 1272px;
  display: flex;
  flex-direction: column;
  grid-template-columns: repeat(12, 1fr);
  display: grid;

  // 2 col
  ${({ variant }) => {
    switch (true) {
      case variant === GALLERY_VARIANTS.compact:
      case variant === GALLERY_VARIANTS.classic:
        return css`
          grid-template-areas:
            '${repeatArea('lot-header', 12)}'
            '${repeatArea('top', 7)} - ${repeatArea('bid', 4)}'
            '${repeatArea('content', 7)} - ${repeatArea('bid', 4)}'
            '${repeatArea('bottom', 12)}';
        `
      case variant === GALLERY_VARIANTS.modern:
      default:
        return css`
          grid-template-areas:
            '${repeatArea('lot-header', 12)}'
            '${repeatArea('top', 12)}'
            '${repeatArea('content', 7)} - ${repeatArea('bid', 4)}'
            '${repeatArea('bottom', 12)}';
        `
    }
  }}

  @media screen and (width < ${LOT_THEME.breakpoints.lg}) {
    grid-template-areas:
      '${repeatArea('lot-header', 12)}'
      '${repeatArea('top', 12)} ${repeatArea('bid', 6)} '
      '${repeatArea('content', 6)} ${repeatArea('bid', 6)}'
      '${repeatArea('bottom', 12)}';
  }
  @media screen and (width < ${LOT_THEME.breakpoints.md}) {
    grid-template-areas:
      '${repeatArea('lot-header', 12)}'
      '${repeatArea('top', 12)}'
      '${repeatArea('content', 12)}'
      '${repeatArea('bottom', 12)}'
      '${repeatArea('bid', 12)}';
  }

  section:has(h2) {
    position: relative;
    article {
      z-index: 7;

      background-color: #fffffc;
      box-shadow: 2px 2px 4px 1px ${theme.palette.gumLeafPale};
      border-top: 1px solid ${theme.palette.gumLeafPale};
      padding: 16px;
    }
    header fieldset {
      z-index: ${LOT_THEME.zIndex.heading};
    }

    h2 {
      position: sticky;
      top: 0;
      background-color: #fffffc;
      cursor: default;
      z-index: ${LOT_THEME.zIndex.top};
      right: 80px;
      width: fit-content;
      will-change: transform;
      transition: transform 0.1s linear;

      @media screen and (width < ${LOT_THEME.breakpoints.md}) {
        border-bottom-right-radius: 8px;
      }
    }
  }

  h2 {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    align-content: center;
  }
  h4 {
    font-size: 16px;
    font-weight: 400;
    line-height: 18px;
  }
  p {
    font-size: 16px;
    line-height: 24px;
  }

  li {
  }
`

export const FullSection = styled.section`
  grid-area: top;
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
`

export const BottomFullSection = styled.section`
  grid-area: bottom;
  margin-top: 0px;
  padding-top: 32px;
  display: grid;
  grid-auto-flow: row;
  grid-template-areas: "'AddedServices' 'SmartMatch'";
  gap: 32px;
  border-top: 1px solid ${LOT_THEME.divider};
`

export const ContentWithBidSection = styled.section`
  display: flex;
`

export const ContentCol = styled.section`
  grid-area: content;
  display: grid;
  grid-auto-flow: row;
  gap: 32px;
  display: flex;
  flex-direction: column;

  header,
  article:not(:first-child),
  article:not(:last-child) {
    border-bottom: 1px solid ${LOT_THEME.divider};
    padding-bottom: 32px;
  }
`

export const BidCol = styled.section`
  position: relative;
  grid-area: bid;
  @media screen and (min-width: ${LOT_THEME.breakpoints.md}) {
  }
  @media screen and (max-width: ${LOT_THEME.breakpoints.md}) {
    padding: 8px 0 0;
    min-height: 1px;
    z-index: 20; // below modals
    visibility: hidden;

    &:has(.fixed.visible) {
      min-height: 450px;
    }
  }

  &.fixed {
    @media screen and (max-width: ${LOT_THEME.breakpoints.md}) {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      z-index: 999;
      padding: 0 0 env(safe-area-inset-bottom);

      & > :first-child {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      &:not(:focus-within) {
        /* transform: translateY(calc(100% - 40px)); */
      }
    }
  }
`

export const Aside = styled.aside<{ variant: GalleryVariant }>`
  grid-area: live;
  display: flex;
  align-items: flex-end;
  float: right;
  position: sticky;
  top: 32px;
  width: 30px;
  overflow: visible;

  transform: translate(calc(100% + 40px), 0px);

  display: none;
  transition: opacity 1s ease;
  @media screen and (width > calc(${LOT_THEME.breakpoints.xl} )) {
    display: inherit;
    opacity: 0;
  }
  @media screen and (width < calc(${LOT_THEME.breakpoints.xl} )) {
    display: none;
  }
  @media screen and (width > calc(${LOT_THEME.breakpoints.xl} + 80px)) {
    opacity: 1;
  }

  ${({ variant }) => {
    switch (variant) {
      case GALLERY_VARIANTS.modern:
        return css`
          height: 100%;
          position: relative;
          div {
            height: 12px;
          }
          fieldset {
            position: sticky;
            top: 48px;
          }
        `
      case GALLERY_VARIANTS.compact:
      case GALLERY_VARIANTS.classic:
        return css`
          height: 100%;
          position: relative;
          div {
            height: 0px;
          }
          fieldset {
            position: sticky;
            top: 94px;
          }
        `
      default:
        return ''
    }
  }}
  fieldset {
    flex-direction: column-reverse;
  }
`

export const UserSpecs = styled(UnstyledButton)`
  width: 30px;
`
export { Li } from '../ContentHeader/content-header.styles'

export const LiveMetrics = ({ variant, lotData, views, watching, setIsWatching, isWatching, ...props }) => {
  const [{ sideStats }] = useContext(LotSettingsContext)
  if (!sideStats) return null

  return (
    <Aside variant={variant}>
      <div />
      <MetricItems
        views={views}
        watching={watching}
        setIsWatching={setIsWatching}
        isWatching={isWatching}
        enableSpecs={true}
        lotData={lotData}
        {...props}
      />
    </Aside>
  )
}
export const MobileOnly = styled.div`
  display: none;
  @media screen and (width < ${LOT_THEME.breakpoints.md}) {
    display: inherit;
  }
`

//# crazy background
/* background-image: radial-gradient(
        closest-side,
        transparent 98%,
        rgba(0, 0, 0, 0.3) 9.9%
      ),
      radial-gradient(closest-side, transparent 9.8%, rgba(0, 0, 0, 0.3) 50%);
    background-size: 80px 80px;
    background-position: 0 0, 40px 40px; */
    